import React, { useEffect, useMemo, useRef, useState } from 'react';
import * as XLSX from 'xlsx';
import { API_URL } from '../../../constants/api';
import { decodeToken, getToken } from '../../../utils/TokenUtils';
import { exportElementToPDF } from '../../../utils/ktcUtils';
import { notification } from 'antd';

const IncomeStatement = ({ report, link, value }) => {
  const reportRef = useRef(null);
  const token = getToken();
  const companyName = decodeToken(token, 'companyName');
  const companyBanner = decodeToken(token, 'banner');
  const currentDate = new Date().toLocaleDateString();
  console.log('value:', value);
  /* ---------------- helpers ---------------- */
  const parseAmount = (v) =>
    typeof v === 'string'
      ? parseFloat(v.replace(/[^0-9.-]/g, ''))
      : Number(v || 0);

  const formatAmount = (v) =>
    `$${Number(v).toLocaleString(undefined, { minimumFractionDigits: 2 })}`;

  const formatRatio = (v) => {
    if (isNaN(v)) {
      return '';
    }

    return `${Number(v).toLocaleString(undefined, { minimumFractionDigits: 2 })}%`;
  };

  /* ---------------- BUILD SINGLE JSON ---------------- */
  const statement = useMemo(() => {
    if (!Array.isArray(report)) return [];

    const revenuesRaw = report
      .filter((r) => r.AccountType === 'SubRevenue')
      .map((r) => ({
        accountId: r.account_id,
        mainAccountId: r.main_account_id,
        label: r.Account,
        amount: parseAmount(r.Amount),
        level: 1,
      }))
      .sort((a, b) => b.amount - a.amount);

    const expensesRaw = report
      .filter((r) => r.AccountType === 'SubExpense')
      .map((r) => ({
        accountId: r.account_id,
        mainAccountId: r.main_account_id,
        label: r.Account,
        amount: parseAmount(r.Amount),
        level: 1,
      }))
      .sort((a, b) => b.amount - a.amount);

    const totalRevenue = revenuesRaw.reduce((s, r) => s + r.amount, 0);
    const totalExpense = expensesRaw.reduce((s, r) => s + r.amount, 0);

    // ✅ add ratio (% of section total)
    const revenues = revenuesRaw.map((r) => ({
      ...r,
      ratio: (totalRevenue ? (r.amount / totalRevenue) * 100 : 0).toFixed(2),
    }));

    const expenses = expensesRaw.map((r) => ({
      ...r,
      ratio: (totalExpense ? (r.amount / totalExpense) * 100 : 0).toFixed(2),
    }));
    const profitLoss = totalRevenue - totalExpense;

    return [
      {
        section: 'Income',
        rows: revenues,
        total: totalRevenue,
      },
      {
        section: 'Expense',
        rows: expenses,
        total: totalExpense,
      },
      {
        section: '',
        rows: [
          {
            label: profitLoss >= 0 ? 'Net Profit' : 'Net Loss',
            amount: profitLoss,
            level: 0,
          },
        ],
      },
    ];
  }, [report]);

  /* ---------------- EXPORT HELPERS ---------------- */
  const flattenForExport = () => {
    const rows = [];

    statement.forEach((sec) => {
      rows.push({ Account: sec.section, Amount: '' });

      sec.rows.forEach((r) => {
        rows.push({
          Account: ' '.repeat(r.level * 4) + r.label,
          Amount: r.amount,
        });
      });

      if (sec.total !== undefined) {
        rows.push({
          Account: `Total ${sec.section}`,
          Amount: sec.total,
        });
      }

      rows.push({ Account: '', Amount: '' });
    });

    return rows;
  };

  /* ---------------- EXPORT ACTIONS ---------------- */
  const exportExcel = () => {
    const rows = flattenForExport();
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Income Statement');
    XLSX.writeFile(wb, 'Income Statement as of {value.from} - {value.to}.xlsx');
  };

  const exportCSV = () => {
    const rows = flattenForExport();
    const csv =
      'Account,Amount\n' +
      rows.map((r) => `"${r.Account}",${r.Amount}`).join('\n');

    const blob = new Blob([csv], { type: 'text/csv' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = 'Income Statement as of {value.from} - {value.to}.csv';
    link.click();
  };

  const exportPDF = async () => {
    try {
      await exportElementToPDF({
        element: reportRef.current,
        fileName: `Income Statement as of {value.from} - {value.to}.pdf`,
        orientation: 'p',
      });
    } catch (err) {
      notification.error({
        message: 'PDF Export Failed',
        description: err.message,
      });
    }
  };

  /* ---------------- UI ---------------- */
  return (
    <>
      {/* Actions */}
      <div style={{ marginTop: 20, textAlign: 'center' }}>
        <button className="btn btn-success me-2" onClick={exportExcel}>
          Export Excel
        </button>
        <button className="btn btn-secondary me-2" onClick={exportCSV}>
          Export CSV
        </button>
        <button className="btn btn-danger" onClick={exportPDF}>
          Export PDF
        </button>
      </div>
      <br />
      <div style={styles.container} ref={reportRef}>
        {/* Header */}
        <div style={styles.headerSection}>
          <img
            src={`${API_URL}/${companyBanner}`}
            alt="Banner"
            style={styles.logo}
          />
          <h2>{companyName}</h2>
          <h3 style={{ fontWeight: 'bold' }}>
            Income Statement as of <br />
            <small>
              {value.from} - {value.to}
            </small>
          </h3>
        </div>

        {/* Table */}
        <table style={styles.table}>
          <tbody>
            {statement.map((sec, i) => (
              <React.Fragment key={i}>
                <tr>
                  <td colSpan="" style={styles.mainAccount}>
                    {sec.section}
                  </td>
                  <td style={styles.totalAmount}>
                    {sec.section === 'Revenue' && 'USD'}
                  </td>
                  <td style={styles.totalAmount}>
                    {sec.section === 'Revenue' && 'Ratio'}
                  </td>
                </tr>

                {sec.rows.map((r, idx) => (
                  <tr key={idx}>
                    <td
                      style={{
                        paddingLeft: `${r.level * 24}px`,
                        fontWeight: r.level === 0 ? 'bold' : 'normal',
                      }}
                    >
                      {r.label}
                    </td>
                    <td style={styles.amount}>
                      {' '}
                      <a
                        target="_blank"
                        rel="noopener noreferrer"
                        href={`/form-report/transactions-list?account_type=${sec.section}&main_account_id=${r.mainAccountId}&account_id=${r.accountId}&from=${value.from}&to=${value.to}`}
                      >
                        {formatAmount(r.amount)}
                      </a>
                    </td>
                    <td style={styles.amount}>{formatRatio(r?.ratio)}</td>
                  </tr>
                ))}

                {sec.total !== undefined && (
                  <tr>
                    <td style={styles.totalLabel}>Total {sec.section}</td>
                    <td style={styles.totalAmount}>
                      {formatAmount(sec.total)}
                    </td>
                    <td style={styles.totalAmount}>{formatRatio(100)}</td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
      <br />
      <br />
    </>
  );
};

/* ---------------- STYLES ---------------- */
const styles = {
  container: {
    width: '35%',
    margin: 'auto',
  },
  headerSection: {
    textAlign: 'center',
    marginBottom: 20,
  },
  logo: {
    width: '100%',
    height: 150,
    objectFit: 'cover',
  },
  table: {
    width: '100%',
  },
  mainAccount: {
    fontWeight: 'bold',
    paddingTop: 10,
  },
  amount: {
    textAlign: 'right',
  },
  totalLabel: {
    fontWeight: 'bold',
    paddingTop: 5,
  },
  totalAmount: {
    fontWeight: 'bold',
    textAlign: 'right',
  },
};

export default IncomeStatement;
